import { Box, Button, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { RiCloseLine } from "react-icons/ri";
import { PersonNameLink } from "src/components/Navigation/PersonNameLink";
import type { ApplicantDuplicate } from "src/generated/hooks/form-service/requests";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { formatBirthDate } from "src/services/format";
import * as Url from "src/services/url";

interface SelectedStudentChipProps {
  // An existing student chosen from the possible-duplicates panel. Shown read-only
  // in place of the create form; its id links the guardians being added.
  applicant: ApplicantDuplicate;
  onClear: () => void;
}

export function SelectedStudentChip({
  applicant,
  onClear,
}: SelectedStudentChipProps) {
  const organization = useOrganization();
  const { glossaryTransform } = useGlossary();
  const fullName = [applicant.first_name, applicant.last_name]
    .filter(Boolean)
    .join(" ");
  const born = formatBirthDate(applicant.birth_date);

  return (
    <Flex
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      padding={4}
      gap={2.5}
      align="flex-start"
    >
      <Box flex="1">
        <Text fontSize="md" fontWeight="700">
          {fullName}
        </Text>
        <Text fontSize="sm" color="gray.700">
          {born && <>Born {born}</>}
          {applicant.guardians.length > 0 && (
            <>
              {born ? " · " : ""}
              Child of{" "}
              {applicant.guardians.map((guardian, index) => (
                <React.Fragment key={guardian.id}>
                  {index > 0 && ", "}
                  <PersonNameLink
                    to={Url.OrgAdmin.Parents.edit(organization, guardian.id)}
                  >
                    {[guardian.first_name, guardian.last_name]
                      .filter(Boolean)
                      .join(" ")}
                  </PersonNameLink>
                </React.Fragment>
              ))}
            </>
          )}
        </Text>
        <Text fontSize="sm" color="gray.700">
          ID: {applicant.id}
        </Text>
        {applicant.reference_id && (
          <Text fontSize="sm" color="gray.700">
            Ref ID {applicant.reference_id}
          </Text>
        )}
      </Box>
      <Button
        type="button"
        variant="outline"
        colorScheme="gray"
        size="sm"
        p="0"
        onClick={onClear}
        aria-label={glossaryTransform("Clear selected student")}
      >
        <RiCloseLine />
      </Button>
    </Flex>
  );
}
